import { Button } from '@casavo/base-ui'
import * as Sentry from '@sentry/nextjs'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import React, { useEffect, useState } from 'react'

import { ContextValue } from '@/context/Cities'
import { ListingCate } from '@/page-commons/listingBySlug'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import getTranslatedTitle from '@/utils/get-translated-title'
import { handleClickShare } from '@/utils/tracking/eventsHandlers'

import Reply from '../Icon/Reply'

import { shareButton } from './styles.css'

type Props = {
  categoryForTracking: string
  cities: ContextValue
  className?: string
  property: ListingCate
}

const ButtonSharing: React.FC<Props> = ({ categoryForTracking, cities, className, property }) => {
  const [nativeShareSupported, setNativeShareSupported] = useState(false)
  const { lang, t } = useTranslation()

  const listingCity = cities.list.find((city) => city.slug === property.detail.city)

  useEffect(() => {
    if (window.navigator && window.navigator.share) {
      setNativeShareSupported(true)
    }
  }, [])

  return (
    <>
      {nativeShareSupported && (
        <Button
          aria-label="Share this listing"
          className={cn(shareButton, className)}
          Icon={(props) => <Reply {...props} />}
          label=""
          onClick={async (event) => {
            event.preventDefault()

            const sellingPrice = formatListingSellingPrice(
              property.detail.privateNegotiation,
              property.detail.sellingPrices,
              t,
              lang
            )
            const typology = getTranslatedTitle(property.detail?.titles, lang).split(' ')[0]
            const area = property.detail.commercialArea
            const city = listingCity.name
            const l10n_in = t('listing-detail:title_1')
            const street = property.addressesCate.street

            const textToShare = `🏡 ${typology} - ${area} mq\n📬 ${city} ${l10n_in} ${street}\n✨ ${sellingPrice}\n\n👇🏻\n`
            const urlToShare = `${document.location.protocol}//${document.location.host}${document.location.pathname}`

            try {
              await window.navigator.share({
                text: textToShare,
                url: urlToShare,
              })
            } catch (err) {
              if (err.toString().includes('AbortError')) {
                // nothing to do here, user canceled the sharing action
                // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share#exceptions
              } else {
                console.error(err)
                Sentry.captureException(err, (scope) => {
                  scope.setExtra('channel', 'lisa.buttonSharing')
                  return scope
                })
              }
            }
            handleClickShare(categoryForTracking)
          }}
        />
      )}
    </>
  )
}

export default ButtonSharing
